import {
  NAVIGATION_EVENTS,
  TEST_EVENTS,
  UTM_EVENTS,
} from "@/constants/analyticsEvents";

import mixpanel from "mixpanel-browser";

// Track if Mixpanel is properly initialized and working
let mixpanelWorking = false;

// Initialize Mixpanel - this should be called once at app startup
export const initMixpanel = () => {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (!token) {
    console.warn("VITE_MIXPANEL_TOKEN not found. Mixpanel tracking disabled.");
    return;
  }

  try {
    mixpanel.init(token, {
      debug: import.meta.env.DEV, // Enable debug in development
      track_pageview: true,
      persistence: "localStorage",
      // Add error handling for blocked requests
      ignore_dnt: true, // Respect Do Not Track
    });

    mixpanelWorking = true; // Set to true after successful initialization
    console.log("Mixpanel initialized successfully");
  } catch (error) {
    console.warn("Failed to initialize Mixpanel:", error);
    mixpanelWorking = false;
  }
};

// Utility functions for common tracking operations
export const analytics = {
  // Identify user - call this when user logs in
  identify: (userId: string) => {
    if (!isMixpanelEnabled()) {
      if (import.meta.env.DEV) {
        console.warn("Mixpanel disabled - no token found");
      }
      return;
    }
    if (!mixpanelWorking) {
      if (import.meta.env.DEV) {
        console.warn("Mixpanel not working - initialization failed");
      }
      return;
    }
    try {
      if (import.meta.env.DEV) {
        console.log("Identifying user:", userId);
      }
      mixpanel.identify(userId);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(
          "Mixpanel identify failed (possibly blocked by ad blocker):",
          error,
        );
      }
    }
  },

  // Set user properties
  setUserProperties: (properties: Record<string, unknown>) => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;
    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.warn("Mixpanel setUserProperties failed:", error);
    }
  },

  // Track events
  track: (eventName: string, properties?: Record<string, unknown>) => {
    if (!isMixpanelEnabled()) {
      if (import.meta.env.DEV) {
        console.warn("Mixpanel disabled - no token found");
      }
      return;
    }
    if (!mixpanelWorking) {
      if (import.meta.env.DEV) {
        console.warn("Mixpanel not working - initialization failed");
      }
      return;
    }
    try {
      // Sanitize properties to ensure they're JSON serializable
      const sanitizedProperties = properties
        ? sanitizeProperties(properties)
        : undefined;

      if (import.meta.env.DEV) {
        console.log("Tracking event:", eventName, sanitizedProperties);
      }
      mixpanel.track(eventName, sanitizedProperties);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(
          "Mixpanel track failed (possibly blocked by ad blocker):",
          error,
        );
      }
    }
  },

  // Track page views (if you want manual control)
  trackPageView: (pageName: string, properties?: Record<string, unknown>) => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;
    try {
      mixpanel.track(NAVIGATION_EVENTS.PAGE_VIEWED, {
        page: pageName,
        ...properties,
      });
    } catch (error) {
      console.warn("Mixpanel trackPageView failed:", error);
    }
  },

  // Set super properties (properties that are sent with every event)
  setSuperProperties: (properties: Record<string, unknown>) => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;
    try {
      mixpanel.register(properties);
    } catch (error) {
      console.warn("Mixpanel setSuperProperties failed:", error);
    }
  },

  // Alias user (useful for connecting anonymous user with identified user)
  alias: (alias: string) => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;
    try {
      mixpanel.alias(alias);
    } catch (error) {
      console.warn("Mixpanel alias failed:", error);
    }
  },

  // Reset user (useful for logout)
  reset: () => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;
    try {
      mixpanel.reset();
    } catch (error) {
      console.warn("Mixpanel reset failed:", error);
    }
  },

  // Check if analytics is working
  isWorking: () => {
    return isMixpanelEnabled() && mixpanelWorking;
  },

  // Debug function to check current state
  getStatus: () => {
    return {
      hasToken: isMixpanelEnabled(),
      isWorking: mixpanelWorking,
      token: import.meta.env.VITE_MIXPANEL_TOKEN ? "***" : "missing",
    };
  },

  // Test function to verify tracking works
  testTrack: () => {
    console.log("Testing Mixpanel tracking...");
    analytics.track(TEST_EVENTS.TEST_EVENT, {
      test_property: "test_value",
      timestamp: new Date().toISOString(),
    });
    console.log("Test event sent");
  },

  // Track UTM campaigns
  trackUTMCampaign: (
    campaignName: string,
    additionalProperties?: Record<string, unknown>,
  ) => {
    if (!isMixpanelEnabled() || !mixpanelWorking) return;

    try {
      const properties = {
        utm_campaign: campaignName,
        page_url: window.location.href,
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...additionalProperties,
      };

      if (import.meta.env.DEV) {
        console.log("Tracking UTM campaign:", campaignName, properties);
      }

      analytics.track(UTM_EVENTS.UTM_CAMPAIGN_LANDING, properties);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("UTM campaign tracking failed:", error);
      }
    }
  },
};

// Helper function to check if Mixpanel is enabled
const isMixpanelEnabled = (): boolean => {
  return !!import.meta.env.VITE_MIXPANEL_TOKEN;
};

// Helper function to sanitize properties for Mixpanel
const sanitizeProperties = (
  properties: Record<string, unknown>,
): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) {
      continue; // Skip null/undefined values
    }

    // Convert Convex IDs and other objects to strings
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        // Handle arrays by converting each element
        sanitized[key] = value.map((item) =>
          typeof item === "object" && item !== null ? String(item) : item,
        );
      } else {
        // Convert objects to strings (handles Convex IDs)
        sanitized[key] = String(value);
      }
    } else {
      // Primitive values are fine as-is
      sanitized[key] = value;
    }
  }

  return sanitized;
};

// Export the raw mixpanel instance if needed for advanced usage
export { mixpanel };
